import { css } from "@emotion/css";
import type { TooltipPosition } from "@octopusdeploy/design-system-components";
import { LegacyListItemButton, Tooltip } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import cn from "classnames";
import * as React from "react";
import MediaQuery from "react-responsive";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { ProcessActionErrorIndicator } from "~/areas/projects/components/Process/ErrorIndicators/ProcessActionErrorIndicator";
import styles from "~/areas/projects/components/Process/ListItems/ProcessListItemForSidebar.module.less";
import Logo from "~/components/Logo";
interface BlueprintSidebarLinkProps {
    className?: string;
    isCurrentAction: boolean;
    onClick: () => void;
    name: string;
    actionType: string | JSX.Element;
    logoUrl?: string;
    icon?: JSX.Element;
    sidebarListing: string;
    children?: React.ReactNode;
    isDisabled: boolean;
    isChild: boolean;
    isRunInParallelWithLast?: boolean;
    stepIndex: number;
    actionIndex?: number;
    containingProcessTemplateStepName: string;
}
export const toolTipBreakpoint = "1279px";
const renderLogoOrIcon = (actionType: string | JSX.Element, logoUrl: string | undefined, isDisabled: boolean, icon: JSX.Element | undefined, toolTipPosition: TooltipPosition | undefined) => {
    return (<Tooltip content={actionType} position={toolTipPosition}>
            <div className={styles.stepLogo}>{logoUrl ? <Logo url={logoUrl} isDisabled={isDisabled} size={"2rem"} className={styles.logo}/> : icon}</div>
        </Tooltip>);
};
const BlueprintSidebarLink: React.FC<BlueprintSidebarLinkProps> = ({ containingProcessTemplateStepName, name, sidebarListing, stepIndex, actionIndex, isCurrentAction, isDisabled, isChild, isRunInParallelWithLast, actionType, logoUrl, icon, onClick, }) => {
    const processErrorSelectors = useProcessErrorSelectors();
    const errors = actionIndex === undefined
        ? processErrorSelectors.getProcessTemplateStepErrors(containingProcessTemplateStepName, stepIndex)
        : processErrorSelectors.getProcessTemplateActionErrors(containingProcessTemplateStepName, stepIndex, actionIndex, isChild);
    return (<StepLink isDisabled={isDisabled} isCurrentAction={isCurrentAction} onClick={onClick}>
            {isRunInParallelWithLast && (<MediaQuery minWidth={toolTipBreakpoint}>
                    {(matches: boolean) => {
                const toolTipPosition = matches ? "left" : undefined;
                return (<div className={styles.stepWrapper}>
                                <Tooltip content="Runs in parallel" position={toolTipPosition}>
                                    <div className={styles.parallelContainer}>
                                        <div className={styles.parallelIcon}>
                                            <div className={styles.content}>&nbsp;</div>
                                        </div>
                                    </div>
                                </Tooltip>
                                {renderLogoOrIcon(actionType, logoUrl, isDisabled, icon, toolTipPosition)}
                            </div>);
            }}
                </MediaQuery>)}
            {!isRunInParallelWithLast && (<MediaQuery minWidth={toolTipBreakpoint}>
                    {(matches: boolean) => {
                const toolTipPosition = matches ? "left" : undefined;
                return renderLogoOrIcon(actionType, logoUrl, isDisabled, icon, toolTipPosition);
            }}
                </MediaQuery>)}
            {withDisabledTooltipWrapper(<div className={styles.stepContainer}>
                    <div className={styles.stepNumber}>{sidebarListing}</div>
                    <div className={styles.stepName}>{name}</div>
                </div>, isDisabled)}
            {errors.length > 0 && (<div className={errorContainer}>
                    <ProcessActionErrorIndicator actionErrors={errors}/>
                </div>)}
        </StepLink>);
};
BlueprintSidebarLink.displayName = "BlueprintSidebarLink"
function withDisabledTooltipWrapper(children: React.ReactNode, isDisabled: boolean) {
    if (isDisabled) {
        return (<Tooltip content="Disabled" className={tooltipStyles}>
                {children}
            </Tooltip>);
    }
    return children;
}
const tooltipStyles = css({
    width: "100%",
});
type StepLinkProps = React.PropsWithChildren<{
    isDisabled: boolean;
    isCurrentAction: boolean;
    onClick: () => void;
}>;
const StepLinkInternal: React.FC<StepLinkProps> = ({ children, isDisabled, isCurrentAction, onClick }) => {
    return (<LegacyListItemButton className={cn(styles.listItem, isCurrentAction ? styles.isActive : null, isDisabled ? styles.isDisabled : null)} onClick={onClick}>
            <div className={styles.listItemLine}/>
            {children}
        </LegacyListItemButton>);
};
StepLinkInternal.displayName = "StepLinkInternal"
const errorContainer = css({
    paddingRight: space[12],
    display: "flex",
    alignItems: "center",
});
const StepLink = React.memo(StepLinkInternal);
export default React.memo(BlueprintSidebarLink);
